import {
    Dispatch,
    Fragment,
    SetStateAction,
    useEffect,
    useMemo,
    useState
} from "react";
import { useSelector } from "react-redux";
import saveAs from "file-saver";

import {
    ArrowDownTrayIcon,
    ArrowPathIcon,
    BarsArrowDownIcon,
    BarsArrowUpIcon,
    BeakerIcon,
    BookOpenIcon,
    CheckCircleIcon,
    ChevronDoubleDownIcon,
    ChevronDoubleUpIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    MinusCircleIcon,
    PencilIcon,
    QuestionMarkCircleIcon,
    TrashIcon,
    XMarkIcon
} from "@heroicons/react/24/outline";
import {
    TbDelta,
    TbDeviceFloppy
} from "react-icons/tb";
import {
    PiFunnel,
    PiFunnelX,
    PiListChecks
} from "react-icons/pi";

import {
    IContextEvalMetrics,
    IContextNoUUID,
    IScrapeEvalMetrics,
    ITemplateExample,
    IScrapeDocumentsNoText
} from "../lib/types";
import { BackendObj } from "../lib/backend";
import {
    classNames,
    downloadAndZipFiles,
    flattenScrapeDocuments,
    prettySmartDateTime,
    redirectToExternalPage,
    redirectToExternalPageWithPostData,
    sleep
} from "../lib/utils";
import { selectMemberships, selectUser } from "../lib/scraper.slice";
import {
    fail_template_example_tags,
    pass_template_example_tags,
    template_example_tags,
    warn_template_example_tags
} from "../lib/consts";

import { ConfirmModal } from "./ConfirmModal";
import { FullScreen } from "./FullScreen";
import { LoadingSpinnerLimit } from "./LoadingSpinner";
import {
    ContextEvalMetrics,
    ScrapeEvalMetrics
} from "./Metrics";
import { TimeoutErrorMessageBar } from "./ErrorMessageBar";
import { ExampleDiffTables } from "./ItemTables";
import {
    Button,
    ButtonGroup,
    IconButton
} from "./Button";
import { ItemContent } from "./ItemContent";
import { ButtonMenu } from "./ButtonMenu";
import { SidePanel } from "./SidePanel";
import { Checkbox } from "./Checkbox";
import {
    Pill,
    PillType
} from "./Pill";
import { EditExampleModal } from "./ExampleModals";
import { Pagination } from "./Pagination";
import {
    getOnboardingProgressStats,
    getOperationProgressStats
} from "./TemplateEvalStats";
import { TemplateOnboarding } from "./TemplateOnboarding";
import {
    IEndpointAttachment,
    IEndpointSlim,
    IExampleStatsForTemplate,
    IOperationStatsForTemplate,
    ITemplateOnboarding,
    IUserSlim
} from "../lib/backend/extractions.types.generated";
import { Textbox } from "./Textbox";
import { Dropdown } from "./Dropdown";
import { DateTime } from "./DateTime";
// import { Dropdown } from "./Dropdown";

function sortTags(tags: string[]) {
    // first we get template_example_tags fro the tags
    const first_tags = template_example_tags.filter((tag) => tags.includes(tag));
    // then we get the rest
    const rest_tags = tags.filter((tag) => !template_example_tags.includes(tag));
    // then we return the sorted tags
    return [...first_tags, ...rest_tags];
}

type TagPillProps = {
    tag: string;
    disabled?: boolean;
    selected?: boolean;
    negated?: boolean;
    color?: "green" | "red";
    onClick?: () => void;
}

export function TagPill({ tag, disabled, selected, negated, color, onClick }: TagPillProps) {
    const type: PillType = (color === "red" || fail_template_example_tags.includes(tag)) ? "error" :
        (color === "green" || pass_template_example_tags.includes(tag)) ? "success" :
            warn_template_example_tags.includes(tag) ? "warning" :
                "info";

    return <Pill
        text={tag}
        type={type}
        disabled={disabled}
        selected={selected}
        onClick={onClick}
        strikethrough={negated}
    />;
}

type ExampleTagPillProps = {
    example_tags: string[];
    green_tags: string[];
    red_tags: string[];
}

function ExampleTagPills({ example_tags, green_tags, red_tags }: ExampleTagPillProps) {
    // we reorder tags, first the green, then the red, then the rest
    const tags = [
        ...example_tags.filter((tag) => green_tags.includes(tag)),
        ...example_tags.filter((tag) => red_tags.includes(tag)),
        ...example_tags.filter((tag) => !green_tags.includes(tag) && !red_tags.includes(tag))
    ];

    return <Fragment>
        {tags.slice(0, 3).map((tag, tag_idx) => <TagPill
            key={tag_idx}
            tag={tag}
            color={green_tags.includes(tag) ? "green" : red_tags.includes(tag) ? "red" : undefined}
        />)}
        {tags.length > 3 && <span className="font-bold text-gray-400">...</span>}
    </Fragment>;
}

export interface IExample {
    item_uuid: string;
    example: ITemplateExample;
    metrics?: IScrapeEvalMetrics[]
    is_modified: boolean;
}

type SaveConfirmationDialogProps = {
    open: boolean;
    contexts: (IContextNoUUID & { uuid: string })[];
    examples?: IExample[];
    onClose: (result: boolean) => void;
}

function SaveConfirmationDialog(props: SaveConfirmationDialogProps) {
    const { open, contexts, examples, onClose } = props;

    const [metric_stats, setMetricStats] = useState<{
        example_idx: number;
        new_records: number;
        missing_records: number;
        modified_fields_count: Record<string, number>;
    }[]>([]);

    useEffect(() => {
        if (examples === undefined) { setMetricStats([]); return; }
        // go over examples and check which confirmation screen fields are modified
        const new_metric_stats: {
            example_idx: number;
            new_records: number;
            missing_records: number;
            modified_fields_count: Record<string, number>;
        }[] = [];
        for (const [example_idx, _example] of examples.entries()) {
            const { example, metrics } = _example;
            // if no metrics, skip
            if (metrics === undefined) { continue; }
            // go over metrics
            const new_metric_stats_for_example: {
                new_records: number;
                missing_records: number;
                modified_fields_count: Record<string, number>;
            } = {
                new_records: 0,
                missing_records: 0,
                modified_fields_count: {}
            };
            for (const metric of metrics) {
                const { new_scrape } = metric;
                const context = contexts.find((context) => context.uuid === new_scrape.context_uuid);
                // we skip if context is not found or if context is not on confirmation screen
                if (context === undefined || context.extract_params.skip_on_confirm) { continue; }
                const old_scrape = example.item.scrapes.find((scrape) => scrape.context_uuid === context.uuid);
                // if no old scrape, then nothing to overwrite, skip
                if (old_scrape === undefined) { continue; }
                // check if any old records are missing or new records are added
                const new_records = metric.diffs.filter((diff) => diff.type === "extra").length;
                const missing_records = metric.diffs.filter((diff) => diff.type === "missing").length;
                // check if any approx records changed confirmation screen fields
                const modified_fields_count: Record<string, number> = {};
                const approx_record_diffs = metric.diffs.filter((diff) => diff.type === "approx");
                for (const field of context.fields) {
                    if (field.skip_on_confirm) { continue; }
                    const old_field_name = old_scrape.field_name_uuid_pairs.find((pair) => pair.uuid === field.uuid)?.name ?? field.name;
                    const new_field_name = new_scrape.field_name_uuid_pairs.find((pair) => pair.uuid === field.uuid)?.name ?? field.name;
                    for (const approx_record_diff of approx_record_diffs) {
                        const old_record = old_scrape.records[approx_record_diff.idx_old];
                        const new_record = new_scrape.records[approx_record_diff.idx_new];
                        const old_field_value = old_record.val[old_field_name];
                        const new_field_value = new_record.val[new_field_name];
                        if (old_field_value !== new_field_value) {
                            modified_fields_count[`[${context.name}] ${field.name}`] = (modified_fields_count[`[${context.name}] ${field.name}`] ?? 0) + 1;
                        }
                    }
                }
                // remember metrics stats
                new_metric_stats_for_example.new_records += new_records;
                new_metric_stats_for_example.missing_records += missing_records;
                new_metric_stats_for_example.modified_fields_count = {
                    ...new_metric_stats_for_example.modified_fields_count,
                    ...modified_fields_count
                };
            }
            if (new_metric_stats_for_example.new_records > 0 ||
                new_metric_stats_for_example.missing_records > 0 ||
                Object.keys(new_metric_stats_for_example.modified_fields_count).length > 0) {

                new_metric_stats.push({
                    example_idx,
                    ...new_metric_stats_for_example
                });
            }
        }
        setMetricStats(new_metric_stats);
    }, [examples, contexts]);

    if (examples === undefined) { return null; }

    return <ConfirmModal
        open={open}
        onClose={onClose}
        title="Save examples"
        size="3xl"
        confirm="Save"
        cancel="Cancel"
        hide_icon={true}
    >
        <div className="py-6 flex flex-col gap-4">
            <div className="text-sm leading-6 text-gray-900 max-h-[500px] overflow-y-auto border border-gray-200 rounded-md">
                {metric_stats.length === 0 ? (
                    <div>No changes detected in confirmation screen fields.</div>
                ) : (
                    <table className="min-w-full">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50">
                                <th className="text-left p-2">Example</th>
                                <th className="text-left p-2 whitespace-nowrap">New rows</th>
                                <th className="text-left p-2 whitespace-nowrap">Del rows</th>
                                <th className="text-left p-2">Modified fields</th>
                            </tr>
                        </thead>
                        <tbody>
                            {metric_stats.map((stat, idx) => (
                                <Fragment key={idx}>
                                    {examples[stat.example_idx] && <tr className="border-b border-gray-100">
                                        <td className="p-2 max-w-[200px] truncate block">{examples[stat.example_idx].example.item.name}</td>
                                        <td className="p-2 align-top text-center">{stat.new_records}</td>
                                        <td className="p-2 align-top text-center">{stat.missing_records}</td>
                                        <td className="p-2 w-1/2">
                                            {Object.entries(stat.modified_fields_count).map(([field, count], i) => (
                                                <div key={field}>{field}: {count}</div>
                                            ))}
                                        </td>
                                    </tr>}
                                </Fragment>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    </ConfirmModal >;
}

type TemplateEvalProps = {
    // template
    org_uuid: string;
    template_uuid: string;
    contexts: (IContextNoUUID & { uuid: string })[];
    disabled: boolean;
    // evaluation data
    examples_offset: number;
    examples_total: number;
    examples?: IExample[];
    tags?: string[];
    latest_onboarding?: ITemplateOnboarding;
    contexts_metrics?: IContextEvalMetrics[];
    example_stats?: IExampleStatsForTemplate;
    operation_stats?: IOperationStatsForTemplate;
    // callbacks
    handleEvaluateTemplate: (example_item_uuids: string[]) => Promise<string | undefined>;
    setExamplesOffset: Dispatch<SetStateAction<number>>;
    setExamplesTotal: Dispatch<SetStateAction<number>>;
    setExamples: Dispatch<SetStateAction<IExample[] | undefined>>;
    setTags: Dispatch<SetStateAction<string[] | undefined>>;
    setContextsMetrics: Dispatch<SetStateAction<IContextEvalMetrics[] | undefined>>;
    setExampleStats: Dispatch<SetStateAction<IExampleStatsForTemplate | undefined>>;
    setOperationStats: Dispatch<SetStateAction<IOperationStatsForTemplate | undefined>>;
    setLatestOnboarding: Dispatch<SetStateAction<ITemplateOnboarding | undefined>>;
    setIsProcessing: (is_processing: boolean) => void;
}

export function TemplateEval(props: TemplateEvalProps) {
    const {
        org_uuid, template_uuid, contexts, disabled, examples, tags, contexts_metrics, example_stats, operation_stats, latest_onboarding,
        handleEvaluateTemplate, setExamples, setTags, setContextsMetrics, setExampleStats, setOperationStats, setLatestOnboarding, setIsProcessing,
        examples_offset, examples_total, setExamplesOffset, setExamplesTotal
    } = props;

    const user = useSelector(selectUser);
    const is_admin = user.role === "admin";
    const memberships = useSelector(selectMemberships);
    const can_edit = memberships.some((membership) => membership.org.uuid === org_uuid && membership.role === "editor") ||
        memberships.some((membership) => membership.org.uuid === org_uuid && membership.role === "admin") || is_admin;

    const [examples_limit, _setExamplesLimit] = useState<number>(parseInt(localStorage.getItem(`template_eval_examples_limit`) ?? "10")); // global setting for the user
    const [endpoints, setEndpoints] = useState<IEndpointSlim[]>([]);
    const [open_examples_uuids, _setOpenExamplesUuids] = useState<string[]>(JSON.parse(localStorage.getItem(`template_eval_open_examples_${template_uuid}`) ?? "[]")); // item_uuid
    const [loaded_examples_uuids, setLoadedExamplesUuids] = useState<string[]>([]); // item_uuid
    const [show_context_metrics, _setShowContextMetrics] = useState<boolean>(localStorage.getItem(`template_eval_show_context_metrics_${template_uuid}`) === "true"); // first time false
    const [hidden_contexts, _setHiddenContexts] = useState<string[]>(JSON.parse(localStorage.getItem(`template_eval_hidden_contexts_${template_uuid}`) ?? "[]")); // context_uuid
    const [hidden_fields, _setHiddenFields] = useState<string[]>(JSON.parse(localStorage.getItem(`template_eval_hidden_fields_${template_uuid}`) ?? "[]")); // field_uuid
    const [show_diff, _setShowDiff] = useState<boolean>((localStorage.getItem(`template_eval_show_diff_${template_uuid}`) ?? "true") === "true"); // first time true
    const [show_full_tables, _setShowFullTables] = useState<boolean>((localStorage.getItem(`template_eval_show_full_tables_${template_uuid}`) ?? "true") === "true"); // first time true
    const [save_examples, setSaveExamples] = useState<IExample[] | undefined>(undefined);
    const [is_tag_filter_open, _setIsTagFilterOpen] = useState<boolean>(localStorage.getItem(`template_eval_is_tag_filter_open_${template_uuid}`) === "true");
    const [selected_tags, _setSelectedTags] = useState<string[]>(JSON.parse(localStorage.getItem(`template_eval_selected_tags_${template_uuid}`) ?? "[]"));
    const [selected_tags_negated, _setSelectedTagsNegated] = useState<string[]>(JSON.parse(localStorage.getItem(`template_eval_selected_tags_negated_${template_uuid}`) ?? "[]"));
    const [tag_filter_mode, setTagFilterMode] = useState<"AND" | "OR">("OR");
    const [is_loading, setIsLoading] = useState<boolean>(false);
    const [is_processing_examples, setIsProcessingExamples] = useState<string[]>([]);
    const [is_committing, setIsCommitting] = useState<boolean>(false);
    const [is_field_filter_open, setIsFieldFilterOpen] = useState<boolean>(false);
    const [is_edit_example_open, setIsEditExampleOpen] = useState<number>(-1);
    const [is_delete_example_open, setIsDeleteExampleOpen] = useState<number>(-1);
    const [full_screen_example, setFullScreenExample] = useState<ITemplateExample | undefined>(undefined);
    const [error_message, setErrorMessage] = useState<string | undefined>(undefined);
    const [current_job_uuid, setCurrentJobUuid] = useState<string | undefined>(undefined);
    const [items_classification_data, setItemsClassificationData] = useState<Map<string, IScrapeDocumentsNoText>>(new Map());

    // New filter states
    const [timestamp_from, setTimestampFrom] = useState<string>("");
    const [timestamp_to, setTimestampTo] = useState<string>("");
    const [selected_user_uuid, setSelectedUserUuid] = useState<string>("");
    const [example_name_includes, setExampleNameIncludes] = useState<string>("");
    const [comment_includes, setCommentIncludes] = useState<string>("");
    const [documents_include, setDocumentsInclude] = useState<string>("");
    const [available_users, setAvailableUsers] = useState<IUserSlim[]>([]);

    const onboarding_progress = useMemo<number | undefined>(() => {
        if (example_stats === undefined) { return undefined; }
        if (latest_onboarding === undefined) { return undefined; }
        return getOnboardingProgressStats(example_stats.example_tags, latest_onboarding);
    }, [example_stats, latest_onboarding]);

    const operation_progress = useMemo<{ jobs_count: number, expected_jobs_count: number } | undefined>(() => {
        if (operation_stats === undefined) { return undefined; }
        if (latest_onboarding === undefined) { return undefined; }
        return getOperationProgressStats(operation_stats.endpoint_job_stats, latest_onboarding);
    }, [operation_stats, latest_onboarding]);

    useEffect(() => {
        BackendObj.extractions.getExtractEndpointsForTemplate({ template_uuid })
            .then(({ endpoints }) => {
                setEndpoints(endpoints);
            })
            .catch(err => console.error(err));
    }, [template_uuid]);

    useEffect(() => {
        if (examples === undefined) {
            setIsLoading(true);
            const selected_item_uuids = [...open_examples_uuids];

            // Convert date strings to timestamps
            const timestamp_from_param = timestamp_from && timestamp_from.trim() ? new Date(timestamp_from).getTime() : undefined;
            const timestamp_to_param = timestamp_to && timestamp_to.trim() ? new Date(timestamp_to).getTime() : undefined;

            // load examples
            BackendObj.extractions.getTemplateExamples({
                template_uuid,
                selected_item_uuids,
                offset: examples_offset,
                limit: examples_limit,
                tag_filter_mode: tag_filter_mode,
                tags_selected: selected_tags,
                tags_negated: selected_tags_negated,
                timestamp_from: timestamp_from_param,
                timestamp_to: timestamp_to_param,
                example_user_uuid: selected_user_uuid && selected_user_uuid.trim() ? selected_user_uuid : undefined,
                example_name_includes: example_name_includes && example_name_includes.trim() ? example_name_includes : undefined,
                comment_includes: comment_includes && comment_includes.trim() ? comment_includes : undefined,
                example_documents_include: documents_include && documents_include.trim() ? documents_include : undefined
            }).then(({ examples: template_examples, total }) => {
                setExamplesTotal(total);
                setExamples(template_examples.map(example => ({
                    item_uuid: example.item.uuid,
                    example: example,
                    is_modified: false
                })));
                // mark which examples were selected to be loaded
                setLoadedExamplesUuids(selected_item_uuids);
            }).catch((err) => {
                setExamples(undefined);
                setErrorMessage(err);
            }).finally(() => setIsLoading(false));
            setItemsClassificationData(new Map());
            // on load metrics are empty
            setContextsMetrics(undefined);
        }
    }, [template_uuid, examples, examples_offset, setExamples, setContextsMetrics, open_examples_uuids, loaded_examples_uuids, setExamplesTotal, tag_filter_mode, selected_tags, selected_tags_negated, examples_limit, timestamp_from, timestamp_to, selected_user_uuid, example_name_includes, comment_includes, documents_include]);

    useEffect(() => {
        if (tags === undefined) {
            // load tags
            BackendObj.extractions.getExampleTagsForTemplate({ template_uuid })
                .then(({ tags }) => {
                    setTags(sortTags(tags));
                })
                .catch((err) => {
                    setTags([]);
                    setErrorMessage(err);
                });
        }
    }, [tags, setTags, template_uuid]);

    useEffect(() => {
        setIsProcessing(is_processing_examples.length > 0);
    }, [is_processing_examples, setIsProcessing]);

    useEffect(() => {
        if (example_stats?.user_stats) {
            // Remove duplicates by using a Map with uuid as key
            const uniqueUsersMap = new Map<string, IUserSlim>();
            example_stats.user_stats.forEach(stat => {
                uniqueUsersMap.set(stat.user.uuid, stat.user);
            });
            setAvailableUsers(Array.from(uniqueUsersMap.values()));
        }
    }, [example_stats]);

    const endpoint_attachments = useMemo<{ title: string, attachment: IEndpointAttachment }[]>(() => {
        const attachments: { title: string, attachment: IEndpointAttachment }[] = [];
        for (const endpoint of endpoints) {
            for (const action of endpoint.actions) {
                if (action.type === "email") {
                    for (const attachment of action.attachments) {
                        attachments.push({
                            title: `${attachment.file_format} (${endpoint.name})`,
                            attachment: attachment
                        });
                    }
                }
            }
        }
        return attachments;
    }, [endpoints]);

    // one or more examples are processing
    const is_processing = is_processing_examples.length > 0;
    const has_modified_examples = examples !== undefined && examples.some((example) => example.is_modified);

    const refreshExamples = (offset: number, limit: number, tag_filter_mode: "AND" | "OR", selected_tags: string[], selected_tags_negated: string[], timestamp_from_val?: string, timestamp_to_val?: string, user_uuid_val?: string, example_name_val?: string, comment_val?: string, documents_val?: string) => {
        setIsLoading(true);
        const selected_item_uuids = [...open_examples_uuids];

        // Convert date strings to timestamps
        const timestamp_from_param = timestamp_from_val && timestamp_from_val.trim() ? new Date(timestamp_from_val).getTime() : undefined;
        const timestamp_to_param = timestamp_to_val && timestamp_to_val.trim() ? new Date(timestamp_to_val).getTime() : undefined;

        BackendObj.extractions.getTemplateExamples({
            template_uuid,
            selected_item_uuids,
            offset,
            limit,
            tag_filter_mode,
            tags_selected: selected_tags,
            tags_negated: selected_tags_negated,
            timestamp_from: timestamp_from_param,
            timestamp_to: timestamp_to_param,
            example_user_uuid: user_uuid_val && user_uuid_val.trim() ? user_uuid_val : undefined,
            example_name_includes: example_name_val && example_name_val.trim() ? example_name_val : undefined,
            comment_includes: comment_val && comment_val.trim() ? comment_val : undefined,
            example_documents_include: documents_val && documents_val.trim() ? documents_val : undefined
        }).then(({ examples: template_examples, total }) => {
            setExamplesTotal(total);
            setExamples(template_examples.map(example => ({
                item_uuid: example.item.uuid,
                example: example,
                is_modified: false
            })));
            // mark which examples were selected to be loaded
            setLoadedExamplesUuids(selected_item_uuids);
        }).catch((err) => {
            setExamples(undefined); setErrorMessage(err);
        }).finally(() => setIsLoading(false));
    }

    const downloadExamples = () => {
        if (examples === undefined) { return; }
        // collect visible fields
        const visible_field_uuids = contexts
            .filter((context) => !hidden_contexts.includes(context.uuid))
            .flatMap((context) => context.fields)
            .filter((field) => !hidden_fields.includes(field.uuid))
            .map((field) => field.uuid);
        // download only open examples
        redirectToExternalPageWithPostData(`/api/items/excel-example`, false, { item_uuids: open_examples_uuids, field_uuids: visible_field_uuids });
    }

    const startProcessingLog = (example_item_uuids: string[]) => {
        setIsProcessingExamples(eis => [...eis, ...example_item_uuids]);
    }

    const stopProcessingLog = (example_item_uuids: string[]) => {
        setIsProcessingExamples(eis => eis.filter((ei) => !example_item_uuids.includes(ei)));
    }

    const onCheck = async (example_item_uuids: string[]) => {
        setErrorMessage(undefined);
        setCurrentJobUuid(undefined); // Clear previous job ID when starting a new one
        startProcessingLog(example_item_uuids);
        let job_uuid: string | undefined = undefined; // Define job_uuid here to use in finally block
        try {
            job_uuid = await handleEvaluateTemplate(example_item_uuids);
            if (job_uuid === undefined) {
                setErrorMessage(`Failed to start template evaluation.`);
                stopProcessingLog(example_item_uuids);
                return;
            }
            setCurrentJobUuid(job_uuid); // Store the new job ID
            const MAX_RETRY_COUNT = 25;
            let retry_count = 0;
            while (retry_count < MAX_RETRY_COUNT) {
                try {
                    console.log("Getting evaluation results for job", job_uuid, "- retry count:", retry_count, new Date());
                    const check_result = await BackendObj.extractions.getEvaluationResults({ job_uuid });
                    console.log("Got evaluation results for job:", job_uuid, ", check_result status:", check_result.status, ", retry count:", retry_count, new Date());
                    if (check_result.status === "done") {
                        if (check_result.template_eval_result && examples !== undefined) {
                            // overall evaluation, update examples and metrics only when more then one example
                            if (example_item_uuids.length > 1) {
                                setContextsMetrics(check_result.template_eval_result.contexts_metrics);
                            }
                            // add metrics to examples
                            const all_scrape_metrics = check_result.template_eval_result.contexts_metrics
                                .flatMap((context_metrics) => context_metrics.scrapes)
                                .filter((scrape) => scrape !== null);
                            setExamples(old_examples => {
                                if (old_examples === undefined) { return undefined; }
                                // if there is a classifier we need to remember the page classifications
                                if (contexts.some((context) => context.type === "classifier")) {
                                    const items_classifications: IScrapeDocumentsNoText[] = check_result.template_eval_result?.items_classification_data ?? [];
                                    const new_items_classification_data: Map<string, IScrapeDocumentsNoText> = new Map();
                                    for (const item_classification of items_classifications) {
                                        new_items_classification_data.set(item_classification.item_uuid, item_classification);
                                    }
                                    setItemsClassificationData(new_items_classification_data);
                                }
                                // REMARK: if we had previous classification data, we could compare it and detect a change
                                // but that data is currently not available (item documents are only loaded on demand)
                                const new_examples = old_examples.map((example) => {
                                    const metrics = all_scrape_metrics.filter((scrape) => scrape.new_scrape.input_item_uuid === example.item_uuid);
                                    return metrics.length > 0 ? {
                                        ...example,
                                        metrics: metrics,
                                        is_modified: true
                                    } : example;
                                });
                                return new_examples;
                            });
                        } else {
                            setErrorMessage(`Failed to get template evaluation results.`);
                        }
                        stopProcessingLog(example_item_uuids);
                        break;
                    } else if (check_result.status === "error") {
                        setErrorMessage(check_result.message);
                        stopProcessingLog(example_item_uuids);
                        break;
                    } else if (check_result.status === "cancelled") {
                        setErrorMessage(check_result.message);
                        stopProcessingLog(example_item_uuids);
                        break;
                    }
                } catch (err) {
                    retry_count++;
                    if (retry_count >= MAX_RETRY_COUNT) { throw err; }
                    console.error(err, new Date());
                }
                await sleep(1000);
            }
        } catch (err: any) {
            console.error(err);
            setErrorMessage(`Failed to check template.`);
            stopProcessingLog(example_item_uuids);
            setItemsClassificationData(new Map());
        } finally {
            // Clear the job ID if the loop finished or an error occurred before the loop ended
            setCurrentJobUuid(undefined);
        }
    };

    const handleCancelTest = async () => {
        if (!current_job_uuid) { return; }
        setErrorMessage(undefined);
        try {
            await BackendObj.extractions.cancelJob({ id: current_job_uuid });
            setCurrentJobUuid(undefined); // clear the job ID as it's cancelled
        } catch (err: any) {
            console.error("Failed to cancel job:", err);
            setErrorMessage(`Failed to cancel the test job.`);
            // do not flush current_job_uuid
        }
    };

    const handleUpdateExamples = async (result: boolean) => {
        setErrorMessage(undefined);
        // avoid double commit and make sure we have examples to save
        if (!result || save_examples === undefined || is_committing) {
            setSaveExamples(undefined);
            return;
        }
        setIsCommitting(true);
        try {
            for (const example of save_examples) {
                if (example.is_modified) {
                    const new_scrapes = example.metrics !== undefined ?
                        example.metrics.map((metric) => metric.new_scrape) :
                        example.example.item.scrapes;
                    const classification_data = items_classification_data.get(example.item_uuid);
                    const { example: updated_example } = await BackendObj.extractions.updateExample({
                        item_uuid: example.item_uuid,
                        classification_data,
                        scrapes: new_scrapes
                    });
                    // set example as not modified
                    if (updated_example !== undefined) {
                        setExamples(old_examples => {
                            if (old_examples === undefined) { return undefined; }
                            const new_examples = old_examples.map((old_example) => {
                                if (old_example.item_uuid === example.item_uuid) {
                                    return {
                                        item_uuid: old_example.item_uuid,
                                        example: updated_example,
                                        is_modified: false
                                    }
                                }
                                return old_example;
                            });
                            return new_examples;
                        });
                    }
                }
            }
            setItemsClassificationData(new Map());
            setContextsMetrics(undefined);
        } catch (err: any) {
            setErrorMessage(`Failed to save modified examples.`);
        }
        // mark done
        setSaveExamples(undefined);
        setIsCommitting(false);
    }

    const handleDeleteExample = async (result: boolean) => {
        setErrorMessage(undefined);
        // close confirmation dialog
        setIsDeleteExampleOpen(-1);
        setIsCommitting(true);
        try {
            if (result) {
                if (examples === undefined || is_delete_example_open < 0) {
                    // nothing to do
                } else if (is_delete_example_open < examples.length) {
                    const item_uuid = examples[is_delete_example_open].item_uuid;
                    await BackendObj.extractions.deleteExample({ item_uuid });
                    setExamples(old_examples => {
                        if (old_examples === undefined) { return undefined; }
                        return old_examples.filter((example) => example.item_uuid !== item_uuid);
                    });
                } else {
                    console.log("Example index too large", examples.length, is_delete_example_open);
                }
            }
        } catch (err: any) {
            console.error(err);
            setErrorMessage(`Failed to delete example.`);
        }
        setIsCommitting(false);
    }

    const handleCopyToClipboard = async (item_uuid: string) => {
        const { example } = await BackendObj.extractions.getTemplateExample({ item_uuid, load_content: "yes" });
        if (example === undefined) { return; }
        navigator.clipboard.writeText(flattenScrapeDocuments(example.item.documents));
    }

    const updateExampleDocumentsClassification = (example: ITemplateExample) => {
        const classification_data = items_classification_data.get(example.item.uuid);
        const example_documents = example.item.documents;
        if (classification_data !== undefined) {
            for (const [doc_idx, doc] of example_documents.entries()) {
                const classified_doc = classification_data.documents[doc_idx];
                if (classified_doc?.name === doc.name) {
                    for (const [page_idx, page] of doc.pages.entries()) {
                        const classified_page = classified_doc.pages[page_idx];
                        if (classified_page?.name === page.name) {
                            // update page classification
                            page.classification = classified_page.classification;
                        }
                    }
                }
            }
        }
    }

    const handleOpenFullScreenExample = async (example: ITemplateExample) => {
        const { example: full_screen_example } = await BackendObj.extractions.getTemplateExample({ item_uuid: example.item.uuid, load_content: "yes" });
        if (full_screen_example === undefined) { return; }
        updateExampleDocumentsClassification(full_screen_example);
        setFullScreenExample(full_screen_example);
    }

    const setExamplesLimit = (offset: number, limit: number) => {
        _setExamplesLimit(limit);
        setExamplesOffset(offset);
        localStorage.setItem(`template_eval_examples_limit`, limit.toString());
        refreshExamples(offset, limit, tag_filter_mode, selected_tags, selected_tags_negated, timestamp_from, timestamp_to, selected_user_uuid, example_name_includes, comment_includes, documents_include);
    }

    // what to show we store in the local storage so we can restore when switching between tabs
    const setShowContextMetrics = (show: boolean) => {
        _setShowContextMetrics(show);
        localStorage.setItem(`template_eval_show_context_metrics_${template_uuid}`, show.toString());
    }

    const loadExamples = async (item_uuids: string[]) => {
        for (const item_uuid of item_uuids) {
            const { example } = await BackendObj.extractions.getTemplateExample({ item_uuid, load_content: "no" });
            if (example === undefined) { continue; }
            setExamples(old_examples => {
                if (old_examples === undefined) { return undefined; }
                // mark example as loaded
                setLoadedExamplesUuids(old_loaded_examples_uuids => {
                    const loaded_examples_uuids_set = new Set([...old_loaded_examples_uuids, item_uuid]);
                    return Array.from(loaded_examples_uuids_set);
                });
                return old_examples.map(e => (e.item_uuid === item_uuid) ? {
                    ...e,
                    example: example,
                    is_modified: false
                } : e);
            });
        }
    }

    const setOpenExamplesUuids = (new_open_examples_uuids: string[]) => {
        // find which examples were not loaded before
        const need_to_load_uuids = new_open_examples_uuids.filter((uuid) => !loaded_examples_uuids.includes(uuid));
        loadExamples(need_to_load_uuids); // fire-and-forget
        _setOpenExamplesUuids(new_open_examples_uuids);
        localStorage.setItem(`template_eval_open_examples_${template_uuid}`, JSON.stringify(new_open_examples_uuids));
    }

    const setHiddenContexts = (hidden_contexts: string[]) => {
        _setHiddenContexts(hidden_contexts);
        localStorage.setItem(`template_eval_hidden_contexts_${template_uuid}`, JSON.stringify(hidden_contexts));
    }

    const toggleHiddenContext = (checked: boolean, context_uuid: string) => {
        if (checked) {
            setHiddenContexts([...hidden_contexts, context_uuid]);
        } else {
            setHiddenContexts(hidden_contexts.filter((c) => c !== context_uuid));
        }
    }

    const setHiddenFields = (hidden_fields: string[]) => {
        _setHiddenFields(hidden_fields);
        localStorage.setItem(`template_eval_hidden_fields_${template_uuid}`, JSON.stringify(hidden_fields));
    }

    const toggleHiddenField = (checked: boolean, field_uuid: string) => {
        if (checked) {
            setHiddenFields([...hidden_fields, field_uuid]);
        } else {
            setHiddenFields(hidden_fields.filter((f) => f !== field_uuid));
        }
    }

    const setSelectedTags = (tags: string[]) => {
        _setSelectedTags(tags);
        localStorage.setItem(`template_eval_selected_tags_${template_uuid}`, JSON.stringify(tags));
    }

    const setSelectedTagsNegated = (tags: string[]) => {
        _setSelectedTagsNegated(tags);
        localStorage.setItem(`template_eval_selected_tags_negated_${template_uuid}`, JSON.stringify(tags));
    }

    const handleTagClick = (tag: string) => {
        if (selected_tags.includes(tag)) {
            const new_selected_tags = selected_tags.filter((t) => t !== tag);
            setSelectedTags(new_selected_tags);
            const new_selected_tags_negated = [...selected_tags_negated, tag];
            setSelectedTagsNegated(new_selected_tags_negated);
            refreshExamples(examples_offset, examples_limit, tag_filter_mode, new_selected_tags, new_selected_tags_negated, timestamp_from, timestamp_to, selected_user_uuid, example_name_includes, comment_includes, documents_include);
        } else if (selected_tags_negated.includes(tag)) {
            const new_selected_tags_negated = selected_tags_negated.filter((t) => t !== tag);
            setSelectedTagsNegated(new_selected_tags_negated);
            refreshExamples(examples_offset, examples_limit, tag_filter_mode, selected_tags, new_selected_tags_negated, timestamp_from, timestamp_to, selected_user_uuid, example_name_includes, comment_includes, documents_include);
        } else {
            const new_selected_tags = [...selected_tags, tag];
            setSelectedTags(new_selected_tags);
            refreshExamples(examples_offset, examples_limit, tag_filter_mode, new_selected_tags, selected_tags_negated, timestamp_from, timestamp_to, selected_user_uuid, example_name_includes, comment_includes, documents_include);
        }
    }

    const selectAllContextsFields = () => {
        setHiddenContexts([]);
        setHiddenFields([]);
    }

    const deselectAllContextsFields = () => {
        setHiddenContexts(contexts.map((context) => context.uuid));
        setHiddenFields(contexts.flatMap((context) => context.fields.map((field) => field.uuid)));
    }

    const selectConfirmationScreenContextsFields = () => {
        setHiddenContexts(contexts
            .filter((context) => context.extract_params.skip_on_confirm)
            .map((context) => context.uuid));
        setHiddenFields([
            // for contexts not hidden, hide fields that are skipped on confirmation
            ...contexts
                .filter((context) => !context.extract_params.skip_on_confirm)
                .flatMap((context) => context.fields)
                .filter((field) => field.skip_on_confirm)
                .map((field) => field.uuid),
            // for contexts hidden, hide all fields
            ...contexts
                .filter((context) => context.extract_params.skip_on_confirm)
                .flatMap((context) => context.fields)
                .map((field) => field.uuid)
        ]);
    }

    const setShowDiff = (show: boolean) => {
        _setShowDiff(show);
        localStorage.setItem(`template_eval_show_diff_${template_uuid}`, show.toString());
    }

    const setShowFullTables = (show: boolean) => {
        _setShowFullTables(show);
        localStorage.setItem(`template_eval_show_full_tables_${template_uuid}`, show.toString());
    }

    const setIsTagFilterOpen = (open: boolean) => {
        _setIsTagFilterOpen(open);
        localStorage.setItem(`template_eval_is_tag_filter_open_${template_uuid}`, open.toString());
    }

    const clearFilter = () => {
        setSelectedTags([]);
        setSelectedTagsNegated([]);
        setTimestampFrom("");
        setTimestampTo("");
        setSelectedUserUuid("");
        setExampleNameIncludes("");
        setCommentIncludes("");
        refreshExamples(examples_offset, examples_limit, tag_filter_mode, [], [], "", "", "", "", "", "");
    }

    const downloadAttachments = async (idx: number, example_item_uuids: string[]) => {
        const { attachment_uuids } = await BackendObj.extractions.listExamplesAttachments({ example_item_uuids });
        const attachment_urls = attachment_uuids.map((uuid) => `/api/admin/attachment/get?uuid=${uuid}`);
        const zip_filename = `examples_${template_uuid}_${idx}.zip`;
        const zip_uint8array = await downloadAndZipFiles(attachment_urls);
        saveAs(zip_uint8array, zip_filename);
    }

    const handleExampleUpdated = async (updated_example: ITemplateExample) => {
        setExamples(current_examples => {
            if (!current_examples) return undefined;
            return current_examples.map(example => {
                if (example.item_uuid === updated_example.item.uuid) {
                    return {
                        item_uuid: example.item_uuid,
                        example: updated_example,
                        metrics: example.metrics,
                        is_modified: example.is_modified
                    };
                }
                return example;
            });
        });
        setTags(prev_tags => {
            if (!prev_tags) { return undefined; }
            const new_tags = [...prev_tags];
            for (const example_tag of updated_example.tags) {
                if (!new_tags.includes(example_tag)) {
                    new_tags.push(example_tag);
                }
            }
            return sortTags(new_tags);
        });
        setIsEditExampleOpen(-1);
        // need to refresh onboarding, to reflect new tag structure
        const { onboarding } = await BackendObj.extractions.getTemplateOnboarding({ template_uuid });
        setLatestOnboarding(onboarding);
        // refresh template example tags stats
        const { example_stats, operation_stats } = await BackendObj.extractions.getEvalStatsForTemplate({ template_uuid });
        setExampleStats(example_stats);
        setOperationStats(operation_stats);
    };

    if (examples === undefined) {
        return <div className="px-10 py-32">
            <div className="max-w-4xl text-gray-600 text-sm">
                <LoadingSpinnerLimit />
            </div>
        </div>;
    }

    const open_examples_set = new Set(open_examples_uuids);
    const open_visible_examples_uuids = examples.filter((example) => open_examples_set.has(example.item_uuid)).map((example) => example.item_uuid);

    const green_tags = latest_onboarding?.step === "build" ? latest_onboarding.build.tags.green_tags :
        latest_onboarding?.step === "onboarding" && latest_onboarding.onboarding ? latest_onboarding.onboarding.tags.green_tags :
            latest_onboarding?.step === "operation" && latest_onboarding.operation ? latest_onboarding.operation.tags.green_tags :
                [];
    const red_tags = latest_onboarding?.step === "build" ? latest_onboarding.build.tags.red_tags :
        latest_onboarding?.step === "onboarding" && latest_onboarding.onboarding ? latest_onboarding.onboarding.tags.red_tags :
            latest_onboarding?.step === "operation" && latest_onboarding.operation ? latest_onboarding.operation.tags.red_tags :
                [];
    const onboarding_tags = [...green_tags, ...red_tags];
    const other_tags = tags?.filter((tag) => !onboarding_tags.includes(tag)) ?? [];

    const is_filter_active = selected_tags.length > 0 || selected_tags_negated.length > 0 ||
        timestamp_from.trim() !== "" || timestamp_to.trim() !== "" ||
        selected_user_uuid.trim() !== "" || example_name_includes.trim() !== "" || comment_includes.trim() !== "" ||
        documents_include.trim() !== "";

    return <Fragment>
        <div className="mx-10 max-w-4xl flex flex-col">
            <TemplateOnboarding
                org_uuid={org_uuid}
                template_uuid={template_uuid}
                endpoints={endpoints}
                onboarding_progress={onboarding_progress}
                operation_progress={operation_progress}
                setLatestOnboarding={setLatestOnboarding}
            />

            <div className="text-base font-semibold leading-7 text-gray-600 py-4">
                Template Examples
            </div>

            <div className="flex flex-row items-start gap-x-4">
                <ButtonGroup
                    buttons={[
                        { icon: ChevronDoubleDownIcon, text: "", tooltip: "Expand all", onClick: () => setOpenExamplesUuids(examples.map((example) => example.item_uuid)) },
                        { icon: ChevronDoubleUpIcon, text: "", tooltip: "Collapse all", onClick: () => setOpenExamplesUuids([]) },
                        { icon: BarsArrowDownIcon, text: "", tooltip: "Show full tables without scrolling", onClick: () => setShowFullTables(true), skip: show_full_tables },
                        { icon: BarsArrowUpIcon, text: "", tooltip: "Make long tables scrollable", onClick: () => setShowFullTables(false), skip: !show_full_tables },
                        { icon: PiListChecks, text: "", tooltip: "Filter displayed steps and fields", onClick: () => setIsFieldFilterOpen(true) },
                        { icon: PiFunnel, text: "", tooltip: "Filter displayed tags", onClick: () => setIsTagFilterOpen(!is_tag_filter_open), selected: is_tag_filter_open }
                    ]}
                    tooltip_placement="top"
                    tooltip_id="template_eval_button_group_1"
                />
                <div className="flex-grow" />
                {is_admin && <div>
                    <ButtonMenu title="Admin" items={[
                        current_job_uuid ?
                            { title: "Cancel Test", onClick: handleCancelTest } :
                            { title: "Test open examples", onClick: () => onCheck(open_visible_examples_uuids), disabled: open_visible_examples_uuids.length === 0 },
                        { title: "Download attachments", onClick: () => downloadAttachments(Math.floor(examples_offset / examples_limit), examples.map((example) => example.item_uuid)) }
                    ]} />
                </div>}
                <div>
                    <ButtonGroup
                        buttons={[
                            { icon: ArrowPathIcon, text: "", tooltip: "Reload examples", onClick: () => refreshExamples(examples_offset, examples_limit, tag_filter_mode, selected_tags, selected_tags_negated, timestamp_from, timestamp_to, selected_user_uuid, example_name_includes, comment_includes, documents_include) },
                            { icon: ArrowDownTrayIcon, text: "", tooltip: "Download examples<br>Assumes last saved template", onClick: downloadExamples, disabled: has_modified_examples || open_examples_uuids.length === 0 },
                            { icon: TbDeviceFloppy, text: "", tooltip: "Save all examples", onClick: () => setSaveExamples(examples.filter(({ is_modified }) => is_modified)), disabled: !has_modified_examples }
                        ]}
                        disabled={disabled || is_loading || is_committing || is_processing}
                        tooltip_placement="top"
                        tooltip_id="template_eval_button_group_2"
                    />
                    {has_modified_examples && <div className="pt-2 pr-1 text-sm text-gray-400 text-right">Unsaved changes</div>}
                </div>
            </div>
        </div>

        {is_tag_filter_open && <div className="mx-10 my-6 max-w-4xl rounded-md bg-gray-50 border border-gray-400 px-6 py-4 flex flex-col gap-y-6">
            <div className="flex flex-row items-center gap-x-2 pb-2">
                <span className="text-sm font-semibold text-gray-600">Filter ({examples_total})</span>
                <div className="flex-grow" />
                {is_filter_active && <IconButton icon={PiFunnelX} onClick={clearFilter} />}
                <IconButton icon={XMarkIcon} onClick={() => setIsTagFilterOpen(false)} />
            </div>

            {tags && tags.length > 0 && <div className="flex flex-row items-start gap-x-2">
                <div className="flex-grow flex flex-col gap-y-4">
                    {latest_onboarding && <div className="flex flex-row items-center gap-x-2">
                        {latest_onboarding.step === "build" && <div className="text-xs text-gray-500 self-start pt-1 w-28 flex-shrink-0 whitespace-nowrap">Build tags</div>}
                        {latest_onboarding.step === "onboarding" && <div className="text-xs text-gray-500 self-start pt-1 w-28 flex-shrink-0 whitespace-nowrap">Onboarding tags</div>}
                        {latest_onboarding.step === "operation" && <div className="text-xs text-gray-500 self-start pt-1 w-28 flex-shrink-0 whitespace-nowrap">Operation tags</div>}
                        <div className="flex flex-wrap gap-2">
                            {green_tags.map((tag, tag_idx) => <TagPill
                                key={tag_idx}
                                tag={tag}
                                color="green"
                                onClick={() => handleTagClick(tag)}
                                selected={[...selected_tags, ...selected_tags_negated].includes(tag)}
                                negated={selected_tags_negated.includes(tag)}
                            />)}
                            {red_tags.map((tag, tag_idx) => <TagPill
                                key={tag_idx}
                                tag={tag}
                                color="red"
                                onClick={() => handleTagClick(tag)}
                                selected={[...selected_tags, ...selected_tags_negated].includes(tag)}
                                negated={selected_tags_negated.includes(tag)}
                            />)}
                        </div>
                    </div>}

                    <div className="flex flex-row items-center gap-x-2">
                        {latest_onboarding === undefined && <div className="text-xs text-gray-500 self-start pt-1 w-28 flex-shrink-0 whitespace-nowrap">Tags</div>}
                        {latest_onboarding !== undefined && <div className="text-xs text-gray-500 self-start pt-1 w-28 flex-shrink-0 whitespace-nowrap">Other tags</div>}
                        <div className="flex flex-wrap items-center gap-2">
                            {other_tags.map((tag, tag_idx) => <TagPill
                                key={tag_idx}
                                tag={tag}
                                onClick={() => handleTagClick(tag)}
                                selected={[...selected_tags, ...selected_tags_negated].includes(tag)}
                                negated={selected_tags_negated.includes(tag)}
                            />)}
                        </div>
                    </div>
                </div>
                {tags.length > 1 && <Fragment>
                    <div className="flex-grow" />
                    <div className="flex-shrink-0 flex flex-row items-center gap-x-2 ">
                        <ButtonGroup
                            tiny={true}
                            buttons={[
                                { text: "All", selected: tag_filter_mode === "AND", onClick: () => { setTagFilterMode("AND"); refreshExamples(examples_offset, examples_limit, "AND", selected_tags, selected_tags_negated, timestamp_from, timestamp_to, selected_user_uuid, example_name_includes, comment_includes, documents_include) } },
                                { text: "Any", selected: tag_filter_mode === "OR", onClick: () => { setTagFilterMode("OR"); refreshExamples(examples_offset, examples_limit, "OR", selected_tags, selected_tags_negated, timestamp_from, timestamp_to, selected_user_uuid, example_name_includes, comment_includes, documents_include) } }
                            ]}
                        />
                    </div>
                </Fragment>}
            </div>}

            {/* Additional filters */}
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                <div className="flex flex-row items-center gap-x-2">
                    <span className="text-xs text-gray-500 w-28 flex-shrink-0 whitespace-nowrap">From date</span>
                    <DateTime
                        placeholder="Select start date..."
                        value={timestamp_from}
                        onChange={(value: string) => setTimestampFrom(value)}
                        onBlur={() => {
                            // Auto-apply when focus lost
                            refreshExamples(examples_offset, examples_limit, tag_filter_mode, selected_tags, selected_tags_negated, timestamp_from, timestamp_to, selected_user_uuid, example_name_includes, comment_includes, documents_include);
                        }}
                    />
                </div>
                <div className="flex flex-row items-center gap-x-2">
                    <span className="text-xs text-gray-500 w-28 flex-shrink-0 whitespace-nowrap">To date</span>
                    <DateTime
                        placeholder="Select end date..."
                        value={timestamp_to}
                        onChange={(value: string) => setTimestampTo(value)}
                        onBlur={() => {
                            // Auto-apply when focus lost
                            refreshExamples(examples_offset, examples_limit, tag_filter_mode, selected_tags, selected_tags_negated, timestamp_from, timestamp_to, selected_user_uuid, example_name_includes, comment_includes, documents_include);
                        }}
                    />
                </div>
                <div className="flex flex-row items-center gap-x-2 col-span-2">
                    <span className="text-xs text-gray-500 w-28 flex-shrink-0 whitespace-nowrap">User</span>
                    <Dropdown
                        values={available_users.map((user) => user.first_name + " " + user.last_name + " (" + user.email + ")")}
                        ids={available_users.map((user) => user.uuid)}
                        selected={selected_user_uuid}
                        onChange={(value: string) => {
                            setSelectedUserUuid(value);
                            // Auto-apply when selection changes
                            refreshExamples(examples_offset, examples_limit, tag_filter_mode, selected_tags, selected_tags_negated, timestamp_from, timestamp_to, value, example_name_includes, comment_includes, documents_include);
                        }}
                    />
                </div>
                <div className="flex flex-row items-center gap-x-2 col-span-2">
                    <span className="text-xs text-gray-500 w-28 flex-shrink-0 whitespace-nowrap">Example name</span>
                    <Textbox
                        placeholder="Search example names..."
                        value={example_name_includes}
                        onChange={(value: string) => setExampleNameIncludes(value)}
                        onBlur={() => {
                            // Auto-apply when focus lost
                            refreshExamples(examples_offset, examples_limit, tag_filter_mode, selected_tags, selected_tags_negated, timestamp_from, timestamp_to, selected_user_uuid, example_name_includes, comment_includes, documents_include);
                        }}
                        keyHandlers={{
                            Enter: () => {
                                // Auto-apply when Enter is pressed
                                refreshExamples(examples_offset, examples_limit, tag_filter_mode, selected_tags, selected_tags_negated, timestamp_from, timestamp_to, selected_user_uuid, example_name_includes, comment_includes, documents_include);
                            }
                        }}
                    />
                </div>
                <div className="flex flex-row items-center gap-x-2 col-span-2">
                    <span className="text-xs text-gray-500 w-28 flex-shrink-0 whitespace-nowrap">Comment</span>
                    <Textbox
                        placeholder="Search comments..."
                        value={comment_includes}
                        onChange={(value: string) => setCommentIncludes(value)}
                        onBlur={() => {
                            // Auto-apply when focus lost
                            refreshExamples(examples_offset, examples_limit, tag_filter_mode, selected_tags, selected_tags_negated, timestamp_from, timestamp_to, selected_user_uuid, example_name_includes, comment_includes, documents_include);
                        }}
                        keyHandlers={{
                            Enter: () => {
                                // Auto-apply when Enter is pressed
                                refreshExamples(examples_offset, examples_limit, tag_filter_mode, selected_tags, selected_tags_negated, timestamp_from, timestamp_to, selected_user_uuid, example_name_includes, comment_includes, documents_include);
                            }
                        }}
                    />
                </div>
                <div className="flex flex-row items-center gap-x-2 col-span-2">
                    <span className="text-xs text-gray-500 w-28 flex-shrink-0 whitespace-nowrap">Content</span>
                    <Textbox
                        placeholder="Search documents..."
                        value={documents_include}
                        onChange={(value: string) => setDocumentsInclude(value)}
                        onBlur={() => {
                            // Auto-apply when focus lost
                            refreshExamples(examples_offset, examples_limit, tag_filter_mode, selected_tags, selected_tags_negated, timestamp_from, timestamp_to, selected_user_uuid, example_name_includes, comment_includes, documents_include);
                        }}
                        keyHandlers={{
                            Enter: () => {
                                // Auto-apply when Enter is pressed
                                refreshExamples(examples_offset, examples_limit, tag_filter_mode, selected_tags, selected_tags_negated, timestamp_from, timestamp_to, selected_user_uuid, example_name_includes, comment_includes, documents_include);
                            }
                        }}
                    />
                </div>
            </div>

        </div>}

        {!is_tag_filter_open && is_filter_active && <div className="mx-10 my-6 max-w-4xl flex flex-row items-center gap-x-2">
            <span className="text-sm font-semibold text-gray-600">Active filters ({tag_filter_mode === "AND" ? "all" : "any"}):</span>
            {green_tags.filter(tag => selected_tags.includes(tag) || selected_tags_negated.includes(tag))
                .map((tag, tag_idx) => <TagPill key={tag_idx} tag={tag} color="green" negated={selected_tags_negated.includes(tag)} />)}
            {red_tags.filter(tag => selected_tags.includes(tag) || selected_tags_negated.includes(tag))
                .map((tag, tag_idx) => <TagPill key={tag_idx} tag={tag} color="red" negated={selected_tags_negated.includes(tag)} />)}
            {other_tags.filter(tag => selected_tags.includes(tag) || selected_tags_negated.includes(tag))
                .map((tag, tag_idx) => <TagPill key={tag_idx} tag={tag} negated={selected_tags_negated.includes(tag)} />)}
            <div className="flex-grow" />
            <IconButton icon={PiFunnelX} onClick={clearFilter} />
        </div>}

        {is_admin && contexts_metrics && show_context_metrics && <div className="my-6 mx-10 w-full max-w-4x text-gray-600">
            <div className="px-2 font-medium text-sm flex items-center gap-2 pb-2 text-gray-500">
                Overall percentage change <span className="font-normal">(0% = no change)</span>
            </div>
            <div className="p-1 flex flex-row gap-2 items-center max-w-4xl">
                <ContextEvalMetrics contexts_eval_metrics={contexts_metrics} />
            </div>
        </div>}

        <div className="mt-6 mx-10 flex flex-col items-start text-gray-600">
            {examples && examples.map(({ example, metrics, is_modified }, idx) =>
                <Fragment key={idx}>
                    {open_examples_set.has(example.item.uuid) && <div className="min-w-[56rem]">
                        <div
                            className={classNames(
                                "p-4 max-w-4xl text-sm font-semibold flex flex-row items-center gap-x-4 border-t border-gray-200 truncate cursor-pointer hover:bg-sea_blue-100",
                                (is_processing_examples.includes(example.item.uuid) || is_committing)
                                    ? "bg-gray-200 text-gray-400"
                                    : metrics?.some(context_metric => context_metric.new_scrape?.debug_log?.some(log => log.type === "error"))
                                        ? "bg-torch_red-50"
                                        : "bg-gray-50"
                            )}
                            onClick={() => setOpenExamplesUuids(open_examples_uuids.filter((ei) => ei !== example.item.uuid))}
                        >
                            <div className="flex flex-row items-center min-w-0 flex-shrink-1">
                                <span className="truncate">{idx + 1 + examples_offset}. {example.item.name}</span>
                            </div>
                            <ExampleTagPills example_tags={example.tags} green_tags={green_tags} red_tags={red_tags} />
                            <div className="flex-grow" />
                            {is_modified && !((is_processing_examples.includes(example.item.uuid) || is_committing)) && <span className="text-xs text-gray-400">modified</span>}
                            {(is_processing_examples.includes(example.item.uuid) || is_committing) && <i className="fas fa-spinner fa-spin" />}
                            <span className="text-xs text-gray-400">{example.user.first_name} {example.user.last_name} [{prettySmartDateTime(example.item.created_at, true)}]</span>
                            <ChevronUpIcon className="h-5 w-5 text-gray-400 cursor-pointer" />
                        </div>
                        <div className={classNames("pt-4 px-2 flex flex-row gap-2 items-start max-w-4xl")}>
                            <div className="flex flex-col gap-y-2 text-sm">
                                {example.comments.length > 0 && <div>
                                    <span className="text-xs text-gray-400">[{prettySmartDateTime(new Date(example.comments[example.comments.length - 1].created_at).getTime())}]</span> {example.comments[example.comments.length - 1].comment} {example.comments.length > 1 && <span onClick={() => setIsEditExampleOpen(idx)} className="cursor-pointer">...</span>}
                                </div>}
                            </div>
                            <div className="flex-grow min-w-[40px]" />
                            <ButtonMenu
                                title="Download"
                                menu_width="wide"
                                items={[
                                    {
                                        title: "Excel",
                                        onClick: () => redirectToExternalPageWithPostData("/api/item/excel-example", true, { item_uuid: example.item.uuid }),
                                        disabled: is_modified
                                    },
                                    ...endpoint_attachments.map(({ title, attachment }) => ({
                                        title,
                                        onClick: () => redirectToExternalPageWithPostData("/api/item/endpoint-example", true, { item_uuid: example.item.uuid, attachment }),
                                        disabled: is_modified
                                    })),
                                    {
                                        title: "Copy to clipboard",
                                        onClick: () => handleCopyToClipboard(example.item.uuid),
                                        admin_only: true,
                                        separator: true
                                    },
                                    ...example.attachments.map((attachment, attachment_idx) => ({
                                        title: attachment.filename,
                                        onClick: () => redirectToExternalPage(`/api/attachment/get?uuid=${attachment.uuid}`),
                                        separator: attachment_idx === 0
                                    }))
                                ]}
                            />
                            <ButtonGroup
                                buttons={[
                                    { icon: BeakerIcon, text: "", tooltip: "Test example", onClick: () => onCheck([example.item.uuid]), disabled: is_processing_examples.includes(example.item.uuid) || is_committing },
                                    { icon: BookOpenIcon, text: "", tooltip: "Show input document(s)", onClick: () => handleOpenFullScreenExample(example) },
                                    { icon: PencilIcon, text: "", tooltip: "Edit example", onClick: () => setIsEditExampleOpen(idx), disabled: is_processing_examples.includes(example.item.uuid) || is_committing },
                                    { icon: TbDelta, text: "", tooltip: "Show differences vs. Show validations", onClick: () => setShowDiff(!show_diff), selected: show_diff, disabled: is_processing_examples.includes(example.item.uuid) || is_committing || !is_modified },
                                    { icon: TbDeviceFloppy, text: "", tooltip: "Save example", onClick: () => setSaveExamples([examples[idx]]), disabled: is_processing_examples.includes(example.item.uuid) || is_committing || !is_modified, skip: !can_edit },
                                    { icon: TrashIcon, text: "", tooltip: "Delete example", onClick: () => setIsDeleteExampleOpen(idx), disabled: is_processing_examples.includes(example.item.uuid) || is_committing, skip: !can_edit }
                                ]}
                            />
                        </div>
                        {metrics && show_context_metrics && <div className="w-full max-w-4xl pt-4">
                            <ScrapeEvalMetrics scrapes_eval_metrics={metrics} />
                        </div>}
                        <div className={classNames("py-3", idx < examples.length - 1 ? "" : "border-b border-gray-200")}>
                            <ExampleDiffTables
                                contexts={contexts}
                                item={example.item}
                                scrapes_eval_metrics={metrics}
                                hidden_contexts={hidden_contexts}
                                hidden_fields={hidden_fields}
                                show_diff={show_diff}
                                show_full_tables={show_full_tables} />
                        </div>
                    </div>}

                    {!open_examples_set.has(example.item.uuid) && <div className={classNames("w-full max-w-4xl border-t border-gray-200", idx < examples.length - 1 ? "" : "border-b")}>
                        <div
                            className={classNames(
                                "p-4 text-sm 00 font-semibold flex flex-row items-center gap-x-4 truncate cursor-pointer",
                                (is_processing_examples.includes(example.item.uuid) || is_committing) ? "bg-gray-200 text-gray-400" : "bg-gray-50"
                            )}
                            onClick={() => setOpenExamplesUuids([...open_examples_uuids, example.item.uuid])}
                        >
                            <div className="flex flex-row items-center min-w-0 flex-shrink-1">
                                <span className="truncate">{idx + 1 + examples_offset}. {example.item.name}</span>
                            </div>
                            <ExampleTagPills example_tags={example.tags} green_tags={green_tags} red_tags={red_tags} />
                            <div className="flex-grow" />
                            {is_modified && !((is_processing_examples.includes(example.item.uuid) || is_committing)) && <span className="text-xs text-gray-400">modified</span>}
                            {(is_processing_examples.includes(example.item.uuid) || is_committing) && <i className="fas fa-spinner fa-spin" />}
                            <span className="text-xs text-gray-400">{example.user.first_name} {example.user.last_name} [{prettySmartDateTime(example.item.created_at, true)}]</span>
                            <ChevronDownIcon className="h-5 w-5 text-gray-400 cursor-pointer" />
                        </div>
                    </div>}
                </Fragment>)}
        </div>

        <div className="mx-10 mb-10 max-w-4xl flex flex-row items-center gap-x-2">
            <div className="flex-grow w-full">
                <Pagination
                    offset={examples_offset}
                    limit={examples_limit}
                    total={examples_total}
                    hide_top_line={true}
                    setOffset={(offset) => {
                        setExamplesOffset(offset);
                        refreshExamples(offset, examples_limit, tag_filter_mode, selected_tags, selected_tags_negated, timestamp_from, timestamp_to, selected_user_uuid, example_name_includes, comment_includes, documents_include);
                    }}
                    limit_options={[10, 20, 50, 100, 200, 500]}
                    setLimit={(offset, limit) => setExamplesLimit(offset, limit)}
                />
            </div>
        </div>

        <ConfirmModal
            open={is_delete_example_open >= 0}
            title="Remove example"
            message={["Are you sure you want to remove this example?"]}
            confirm="Remove"
            onClose={handleDeleteExample} />

        <SaveConfirmationDialog
            open={save_examples !== undefined}
            contexts={contexts}
            examples={save_examples ?? []}
            onClose={(result) => handleUpdateExamples(result)}
        />

        <FullScreen show={full_screen_example !== undefined} onClose={() => setFullScreenExample(undefined)}>
            <div className="py-4 ">
                {full_screen_example?.item && <ItemContent
                    org_uuid={org_uuid}
                    item={full_screen_example?.item}
                    attachments={full_screen_example?.attachments ?? []}
                    contexts={contexts}
                />}
            </div>
        </FullScreen>

        <SidePanel
            open={is_field_filter_open}
            title="Steps and Fields Filter"
            onClose={() => setIsFieldFilterOpen(false)}
            options={<ButtonGroup
                buttons={[
                    { icon: CheckCircleIcon, text: "", tooltip: "Select all", onClick: selectAllContextsFields },
                    { icon: MinusCircleIcon, text: "", tooltip: "Deselect all", onClick: deselectAllContextsFields },
                    { icon: QuestionMarkCircleIcon, text: "", tooltip: "Confirmation Screen", onClick: selectConfirmationScreenContextsFields }
                ]}
            />}
        >
            <div>
                <div className="py-4 flex flex-col gap-y-2 text-sm">
                    <div className="flex flex-row items-center gap-x-2">
                        <Checkbox
                            id="show_context_metrics"
                            checked={show_context_metrics}
                            setChecked={(checked) => setShowContextMetrics(checked)}
                        />
                        <label htmlFor="show_context_metrics">Show percentage change metrics</label>
                    </div>
                </div>
                {contexts.map((context, context_idx) =>
                    <div key={context_idx} className="pb-4 flex flex-col gap-y-2 text-sm">
                        <div className="flex flex-row items-center gap-x-2">
                            <Checkbox
                                id={`show_context_metrics_${context_idx}`}
                                checked={!hidden_contexts.includes(context.uuid)}
                                setChecked={(checked) => toggleHiddenContext(!checked, context.uuid)}
                            />
                            <label htmlFor={`show_context_metrics_${context_idx}`}>Step {context_idx + 1}: {context.name}</label>
                        </div>
                        {["array", "object", "lookup_table"].includes(context.type) && context.fields.map((field, field_idx) =>
                            <div key={field_idx} className="pl-4">
                                <div className="flex flex-row items-center gap-x-2">
                                    <Checkbox
                                        id={`show_field_metrics_${context_idx}_${field_idx}`}
                                        checked={!hidden_fields.includes(field.uuid)}
                                        setChecked={(checked) => toggleHiddenField(!checked, field.uuid)}
                                    />
                                    <label htmlFor={`show_field_metrics_${context_idx}_${field_idx}`}>{field.name}</label>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <div className="px-4 py-6 mt-6 border-t border-gray-200 flex justify-end">
                    <Button text="Close" onClick={() => setIsFieldFilterOpen(false)} />
                </div>
            </div>
        </SidePanel>

        {is_edit_example_open >= 0 && <EditExampleModal
            example_item_uuid={is_edit_example_open >= 0 ? examples[is_edit_example_open].item_uuid : undefined}
            template_uuid={template_uuid}
            require_onboarding_tags="none"
            show_other_tags={true}
            onClose={() => setIsEditExampleOpen(-1)}
            onExampleUpdated={handleExampleUpdated}
        />}

        <TimeoutErrorMessageBar message={error_message} clearMessage={() => setErrorMessage(undefined)} />
    </Fragment >;
}
